import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaChartLine, FaHandshake, FaMoneyBillWave, FaSearchDollar, FaExchangeAlt, FaLightbulb, FaBalanceScale, FaTools, FaChartBar, FaShieldAlt, FaNetworkWired, FaEye } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #dc3545;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#0d6efd"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const CorporateFinance = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const faqData = [
        {
            question: "Qu'est-ce que le \"Corporate Finance\" ou la \"Finance d'Entreprise\" ?",
            answer: "La Finance d'Entreprise englobe l'ensemble des décisions financières stratégiques prises par une entreprise pour optimiser sa valeur et gérer ses ressources financières. Cela inclut les décisions d'investissement (où et comment investir), de financement (comment financer les projets et l'activité), de distribution des bénéfices (dividendes) et de gestion du risque financier."
        },
        {
            question: "Pourquoi la Finance d'Entreprise est-elle importante ?",
            answer: "Une gestion financière saine est cruciale pour la pérennité et le développement de toute entreprise. La Finance d'Entreprise permet de : Maximiser la valeur de l'entreprise : Pour les actionnaires ou propriétaires. Assurer la solvabilité et la liquidité : Être capable de faire face à ses obligations financières. Financer la croissance : Trouver les ressources nécessaires pour investir et se développer. Gérer les risques : Identifier et atténuer les risques financiers. Prendre des décisions éclairées : Basées sur des analyses financières rigoureuses."
        },
        {
            question: "Quelle est la différence entre la comptabilité et la finance d'entreprise ?",
            answer: "La comptabilité se concentre sur l'enregistrement, le classement et la synthèse des transactions financières passées de l'entreprise. La Finance d'Entreprise utilise ces informations (et d'autres données) pour prendre des décisions futures concernant l'investissement, le financement, et la gestion du risque. La finance est prospective, la comptabilité est rétrospective."
        },
        {
            question: "À qui s'adresse ce service ?",
            answer: "Ce service s'adresse aux: Entreprises en croissance: Ayant besoin de lever des fonds, d'optimiser leur structure financière, ou d'évaluer des opportunités d'acquisition. Entreprises établies: Cherchant à optimiser leur rentabilité, à restructurer leur capital, ou à préparer une cession. Startups: Ayant besoin de construire un business plan financier solide, de lever des fonds en amorçage ou en série A, B, etc. Entreprises familiales: Préparant une transmission ou une ouverture du capital. Investisseurs: Souhaitant évaluer des cibles d'investissement ou optimiser la gestion financière de leurs participations. Entreprises en difficulté: Cherchant des solutions de refinancement ou de restructuration de dette."
        },
        {
            question: "Quels services de Corporate Finance proposez-vous ?",
            answer: "Notre offre de services couvre l'ensemble des besoins en Finance d'Entreprise : Fusions et Acquisitions : Accompagnement complet dans les opérations de cession, d'acquisition, de fusion, de scission, d'apport partiel d'actif, etc. (côté vendeur ou côté acheteur). Levée de fonds : Recherche et négociation de financements auprès d'investisseurs ou de banques. Évaluation d'entreprise : Détermination de la valeur de l'entreprise dans différents contextes. Ingénierie financière : Optimisation de la structure financière de l'entreprise, structuration d'opérations complexes. Restructuration financière : Accompagnement des entreprises en difficulté. Modélisation financière : Construction de modèles financiers prévisionnels. Due diligence financière: Audit d'acquisition pour le compte d'un acquéreur potentiel. Conseil en financement de projet, en introduction en bourse (IPO) et en relations investisseurs."
        },
        {
            question: "Pouvez-vous m'aider à vendre mon entreprise ?",
            answer: "Oui, nous vous accompagnons à chaque étape du processus de cession : Préparation : Diagnostic de l'entreprise, valorisation, identification des points forts et des axes d'amélioration, préparation du dossier de présentation (\"mémorandum d'information\"). Identification des acquéreurs potentiels : Recherche ciblée d'acquéreurs (industriels, financiers, etc.). Négociation : Assistance dans les négociations avec les acquéreurs potentiels (prix, conditions, garanties, etc.). Due diligence : Accompagnement dans la phase de due diligence (audit d'acquisition) menée par l'acquéreur. Closing : Assistance juridique et fiscale pour finaliser la transaction."
        },
        {
            question: "Pouvez-vous m'aider à acheter une entreprise ?",
            answer: "Oui, nous vous accompagnons dans votre projet d'acquisition : Définition de la cible : Identification des entreprises correspondant à vos critères de recherche. Approche et évaluation préliminaire: prise de contact avec les cibles potentielles et évaluation rapide. Due diligence : Réalisation d'un audit d'acquisition complet pour identifier les risques et les opportunités. Valorisation : Détermination du prix d'acquisition et négociation des conditions. Financement : Recherche et négociation des financements nécessaires à l'acquisition. Closing : Assistance juridique et fiscale pour finaliser la transaction. Intégration post-acquisition: conseil sur l'intégration de la cible acquise."
        },
        {
            question: "Comment déterminez-vous la valeur d'une entreprise ?",
            answer: "Nous utilisons différentes méthodes d'évaluation, en fonction du contexte et du secteur d'activité : Méthodes patrimoniales : Basées sur la valeur des actifs de l'entreprise. Méthodes de rendement : Basées sur la capacité de l'entreprise à générer des bénéfices futurs (actualisation des flux de trésorerie disponibles, \"Discounted Cash Flow\" - DCF). Méthodes comparatives : Basées sur la comparaison avec des entreprises similaires cotées en bourse ou ayant fait l'objet de transactions récentes. Méthode par les options réelles: pour les entreprises avec des opportunités de croissance importantes mais incertaines. Nous combinons généralement plusieurs méthodes pour obtenir une fourchette de valorisation."
        },
        {
            question: "Pouvez-vous m'aider à lever des fonds ?",
            answer: "Oui, nous vous accompagnons dans tout le processus de levée de fonds: Définition du besoin de financement : Quel montant ? Pour quel projet ? Avec quelle structure de financement ? Préparation du dossier de présentation : Business plan financier, pitch deck, executive summary. Identification des investisseurs potentiels : Ciblage des investisseurs ou des banques. Présentation du projet et négociation : Pitch devant les investisseurs, négociation des termes de l'investissement. Closing : Assistance juridique et fiscale pour finaliser l'opération."
        },
        {
            question: "Quelle est votre approche en matière de Corporate Finance ?",
            answer: "Notre approche est basée sur : L'expertise : Nos consultants sont des experts en finance d'entreprise, avec une solide expérience des transactions et des levées de fonds. La personnalisation : Nous adaptons nos services à vos besoins spécifiques et à votre situation. L'indépendance : Nous agissons dans votre seul intérêt, sans conflit d'intérêts. La confidentialité : Nous garantissons la confidentialité de toutes les informations échangées. Le réseau : Nous disposons d'un large réseau de contacts dans le monde de la finance (investisseurs, banques, avocats, etc.)."
        },
        {
            question: "Pouvez-vous m'aider à préparer un business plan financier ?",
            answer: "Oui, l'élaboration de business plans financiers fait partie intégrante de nos services de Corporate Finance."
        }
    ];

    const servicesData = [
        {
            icon: <FaExchangeAlt />,
            title: "Fusions-Acquisitions",
            description: "Conseil à l'achat et à la vente d'entreprises, due diligence, négociation, structuration des opérations de LBO, MBO et OBO.",
            color: "#0d6efd"
        },
        {
            icon: <FaMoneyBillWave />,
            title: "Levée de Fonds",
            description: "Définition de la stratégie de financement, préparation du business plan, identification d'investisseurs potentiels et négociation des conditions.",
            color: "#198754"
        },
        {
            icon: <FaBalanceScale />,
            title: "Restructuration Financière",
            description: "Diagnostic financier, plans de restructuration, renégociation de dettes et accompagnement dans les procédures collectives avec nos partenaires avocats.",
            color: "#dc3545"
        },
        {
            icon: <FaSearchDollar />,
            title: "Évaluation d'Entreprise",
            description: "Valorisation d'entreprises, d'actifs ou de branches d'activité, utilisant des méthodes reconnues comme DCF et multiples.",
            color: "#6f42c1"
        },
        {
            icon: <FaTools />,
            title: "Ingénierie Financière",
            description: "Optimisation de la structure financière (dette, fonds propres) et mise en place d'outils de financement complexes.",
            color: "#fd7e14"
        },
        {
            icon: <FaChartBar />,
            title: "Modélisation Financière",
            description: "Construction de modèles financiers prévisionnels et business plans pour simuler différents scénarios et éclairer vos décisions.",
            color: "#20c997"
        }
    ];

    const advantagesData = [
        {
            icon: <FaLightbulb />,
            title: "Expertise Financière",
            color: "#0d6efd"
        },
        {
            icon: <FaHandshake />,
            title: "Accompagnement Sur-mesure",
            color: "#198754"
        },
        {
            icon: <FaShieldAlt />,
            title: "Confidentialité",
            color: "#dc3545"
        },
        {
            icon: <FaNetworkWired />,
            title: "Réseau de Partenaires",
            color: "#ffc107"
        },
        {
            icon: <FaEye />,
            title: "Vision à 360°",
            color: "#6f42c1"
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">Corporate Finance</h1>
                            <p className="fs-5 mb-4">Expertise stratégique pour vos opérations financières à fort enjeu</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        Corporate Finance
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                Vous souhaitez lever des fonds, acquérir une entreprise, céder une activité, ou optimiser votre structure financière ?
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Chez CINEF-EC, nous vous offrons une expertise complète en Corporate Finance pour vous accompagner dans toutes les étapes de ces opérations complexes et décisives.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Pourquoi choisir notre cabinet pour votre Corporate Finance ?
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaChartLine className="fs-1 text-primary" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Expertise financière et juridique</h5>
                                                <p className="text-muted">
                                                    Nous maîtrisons les aspects financiers, juridiques et fiscaux des opérations de Corporate Finance.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaHandshake className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Accompagnement sur-mesure</h5>
                                                <p className="text-muted">
                                                    Nous adaptons notre intervention à vos besoins spécifiques et à la nature de votre projet.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaShieldAlt className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Confidentialité et indépendance</h5>
                                                <p className="text-muted">
                                                    Nous agissons en toute discrétion et dans le respect de vos intérêts.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaNetworkWired className="fs-1 text-danger" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Réseau de partenaires</h5>
                                                <p className="text-muted">
                                                    Nous travaillons en étroite collaboration avec un réseau d'experts (avocats, banques d'affaires, fonds d'investissement, etc.).
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Nos services de Corporate Finance</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Des solutions financières adaptées à vos projets stratégiques</p>
                        </div>
                    </div>

                    <div className="row mb-4">
                        {servicesData.slice(0, 3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        {servicesData.slice(3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index + 3}>
                                <ServiceCard delay={`${(index + 3) * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Parlons de votre projet financier</h3>
                                <p className="text-white fs-5 mb-4">
                                    Que vous soyez à la recherche de financements, en préparation d'une cession ou d'une acquisition, 
                                    ou que vous souhaitiez optimiser la structure financière de votre entreprise, notre équipe est prête à vous accompagner.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Nous contacter</a>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12 text-center mb-5">
                            <h3 className="fw-bold">Vos avantages</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {advantagesData.map((item, index) => (
                            <div className="col-6 col-md-4 col-lg-2" key={index}>
                                <AdvantageCard>
                                    <AdvantageIcon color={item.color}>{item.icon}</AdvantageIcon>
                                    <p className="fw-bold fs-5 mb-0 text-center">{item.title}</p>
                                </AdvantageCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa" id="faq-section" className={isVisible ? 'visible' : ''}>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h3 className="fw-bold">Questions Fréquentes sur le Corporate Finance</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <FAQContainer>
                                <FAQContent>
                                    {faqData.map((faq, index) => (
                                        <FAQItem key={index} delay={`${index * 0.2}s`}>
                                            <Question onClick={() => toggleFAQ(index)}>
                                                {faq.question}
                                                <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                                            </Question>
                                            {activeIndex === index && <Answer>{faq.answer}</Answer>}
                                        </FAQItem>
                                    ))}
                                </FAQContent>
                            </FAQContainer>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/image-corporate-finance.png") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background:linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default CorporateFinance;